import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GlassCard, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Shield, User, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

const LoginPage = () => {
  const [role, setRole] = useState<'admin' | 'staff'>('staff');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const success = await login(email, password, role);

    if (success) {
      navigate(role === 'admin' ? '/admin' : '/staff');
    } else {
      toast({
        title: 'Login failed',
        description: 'Invalid credentials. Please check your email and password.',
        variant: 'destructive',
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-slate-950 relative overflow-hidden antialiased">
      {/* Dynamic Animated Decorative Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-500/10 dark:bg-emerald-500/5 blur-3xl"
        />
        <motion.div
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-blue-500/15 dark:bg-blue-500/5 blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, 20, 0],
            y: [0, -10, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
          className="absolute top-1/3 left-1/3 w-80 h-80 rounded-full bg-indigo-500/5 blur-3xl"
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center mb-3">
            <img src="/logo.png" alt="Royal 300 Logo" className="h-16 w-auto object-contain" />
          </div>
          <p className="text-sm font-medium tracking-wide text-slate-500 dark:text-slate-400 uppercase">
            Attendance & Staff Portal
          </p>
        </motion.div>

        {/* Role Toggle Selector */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex gap-1.5 mb-6 p-1.5 bg-slate-200/50 dark:bg-slate-900/50 backdrop-blur-md rounded-2xl border border-slate-200/30 dark:border-slate-800/30"
        >
          <button
            type="button"
            onClick={() => setRole('staff')}
            className={`relative flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-colors duration-300 ${
              role === 'staff' ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            {role === 'staff' && (
              <motion.div
                layoutId="activeRoleBG"
                className="absolute inset-0 bg-white dark:bg-slate-800 rounded-xl shadow-md border border-slate-200/50 dark:border-slate-700/50"
                transition={{ type: "spring", stiffness: 350, damping: 25 }}
              />
            )}
            <User className="relative z-10 w-4.5 h-4.5" />
            <span className="relative z-10">Staff Portal</span>
          </button>

          <button
            type="button"
            onClick={() => setRole('admin')}
            className={`relative flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold text-sm transition-colors duration-300 ${
              role === 'admin' ? 'text-blue-700 dark:text-blue-300' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            {role === 'admin' && (
              <motion.div
                layoutId="activeRoleBG"
                className="absolute inset-0 bg-white dark:bg-slate-800 rounded-xl shadow-md border border-slate-200/50 dark:border-slate-700/50"
                transition={{ type: "spring", stiffness: 350, damping: 25 }}
              />
            )}
            <Shield className="relative z-10 w-4.5 h-4.5" />
            <span className="relative z-10">Admin Panel</span>
          </button>
        </motion.div>

        {/* Login Card Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <GlassCard className="border-slate-200/50 dark:border-slate-800/50 shadow-xl shadow-slate-100/50 dark:shadow-none overflow-hidden rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl">
            <CardHeader className="text-center pb-2 pt-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={role}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <CardTitle className="text-2xl font-bold tracking-tight bg-gradient-to-r from-slate-950 to-slate-850 dark:from-white dark:to-slate-250 bg-clip-text text-transparent">
                    {role === 'admin' ? 'Admin Access' : 'Staff Sign In'}
                  </CardTitle>
                  <CardDescription className="mt-1 text-sm text-slate-500">
                    {role === 'admin'
                      ? 'Secure dashboard control'
                      : 'Log in to record your daily tasks'}
                  </CardDescription>
                </motion.div>
              </AnimatePresence>
            </CardHeader>

            <CardContent className="pt-4 pb-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {role === 'admin' ? 'Username / ID' : 'Email Address'}
                  </label>
                  <Input
                    id="email"
                    type={role === 'admin' ? 'text' : 'email'}
                    placeholder={role === 'admin' ? 'Enter admin username' : 'you@example.com'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-xl bg-slate-100/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-700 transition-all font-medium text-slate-900 dark:text-slate-100"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="h-11 rounded-xl bg-slate-100/50 dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 pr-10 focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-700 transition-all font-medium text-slate-900 dark:text-slate-100"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-250 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                    </button>
                  </div>
                </div>

                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="pt-2"
                >
                  <Button
                    type="submit"
                    size="lg"
                    className={`w-full h-12 rounded-xl text-white font-semibold transition-all duration-300 shadow-md ${
                      role === 'admin'
                        ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/10 dark:shadow-none'
                        : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/10 dark:shadow-none'
                    }`}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4.5 h-4.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Authenticating...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    )}
                  </Button>
                </motion.div>
              </form>
            </CardContent>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
